import { useState } from "react";
import axios from "axios";
import { QUIZ } from "@/data/content";
import { Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Quiz = () => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setAnswer = (qid, idx) => {
    if (result) return;
    setAnswers((a) => ({ ...a, [qid]: idx }));
  };

  const submit = async () => {
    if (Object.keys(answers).length < QUIZ.length) {
      toast.error("Please answer every question.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        answers: QUIZ.map((q) => ({
          question_id: q.id,
          answer_index: answers[q.id],
        })),
      };
      const res = await axios.post(`${API}/quiz/submit`, payload);
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("We could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="quiz-page">
      <div className="overline text-[#D4AF37]">Learn — and carry the knowledge forward</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EBE5] mt-4 leading-tight">
        Eight questions about Sinjar.
      </h1>
      <p className="text-[#A19D98] mt-6 leading-relaxed">
        This is not a test. It is a way to listen, learn, and remember. Each
        question is followed by what history actually records.
      </p>

      {result && (
        <div
          className="mt-12 border border-[#D4AF37]/50 p-8 bg-[#111215]"
          data-testid="quiz-result"
        >
          <div className="overline">Your score</div>
          <div className="font-serif-display text-5xl text-[#D4AF37] mt-3">
            {result.score} <span className="text-[#A19D98]">/ {result.total}</span>
          </div>
          <p className="text-[#A19D98] text-sm mt-4 leading-relaxed">
            What matters more than a score is what you do with what you learn.
            Please consider visiting the Memorial and Take Action pages next.
          </p>
          <button
            onClick={reset}
            data-testid="quiz-reset"
            className="mt-6 px-5 py-2 border border-white/20 text-[#F2EBE5] text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500 inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Try again
          </button>
        </div>
      )}

      <div className="mt-16 space-y-14">
        {QUIZ.map((q, qi) => {
          const userIdx = answers[q.id];
          const isCorrect = result && result.correct_ids.includes(q.id);
          return (
            <div key={q.id} data-testid={`quiz-q-${q.id}`}>
              <div className="overline">Question {String(qi + 1).padStart(2, "0")}</div>
              <h3 className="font-serif-display text-xl lg:text-2xl text-[#F2EBE5] mt-3 leading-snug">
                {q.question}
              </h3>
              <div className="mt-6 border-t border-white/10">
                {q.options.map((opt, i) => {
                  const selected = userIdx === i;
                  const showRight = result && result.explanations[q.id] && i === q.options.findIndex((_, idx) =>
                    // We don't have the correct index client-side; rely on isCorrect + selected for marking.
                    false
                  );
                  return (
                    <button
                      key={i}
                      onClick={() => setAnswer(q.id, i)}
                      data-testid={`quiz-${q.id}-opt-${i}`}
                      disabled={!!result}
                      className={`w-full text-left py-4 px-2 border-b border-white/10 flex items-center gap-4 group transition-colors duration-500 ${
                        selected ? "text-[#D4AF37]" : "text-[#F2EBE5] hover:bg-white/[0.03]"
                      } ${result ? "cursor-default" : ""}`}
                    >
                      <span className="overline text-[#6E6B67]">{String.fromCharCode(65 + i)}</span>
                      <span className="text-sm lg:text-base flex-1">{opt}</span>
                      {result && selected && isCorrect && (
                        <Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      )}
                      {result && selected && !isCorrect && (
                        <X className="w-4 h-4 text-[#5E3A3A]" strokeWidth={1.5} />
                      )}
                      {showRight}
                    </button>
                  );
                })}
              </div>
              {result && (
                <div className="mt-4 text-sm text-[#A19D98] italic border-l border-[#D4AF37]/40 pl-4 leading-relaxed">
                  {result.explanations[q.id]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!result && (
        <div className="mt-16">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="quiz-submit"
            className="px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 disabled:opacity-60"
          >
            {submitting ? "Checking…" : "See what history records"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Quiz;
